/* eslint-disable max-statements */

import {
	ADD_PRODUCT_TO_COMPARE,
	CLEAR_ALL_SELECTED_FACETS,
	CLOSE_DRAWER,
	FACETS_SELECTED,
	FACET_NAME_TO_SCROLL_TO,
	FACET_SELECTED,
	GOT_COMPARED_PRODUCTS,
	GOT_LISTER,
	GOT_NEXT_PAGE,
	GOT_PRODUCTS_IN_BASKET,
	IS_LOADING,
	NEW_DISMISS_FILTERS_URL,
	NEW_FILTERS_URL,
	OPEN_FINANCE_SLIDEOUT,
	REMOVE_PRODUCT_FROM_COMPARE,
	SET_ACTIVE_DRAWER,
	SET_EXPERIMENT_SWITCHES,
	SET_PRODUCT_CARD_VIEW,
	SHOW_FILTERED_PRODUCTS,
	UPDATE_CURRENT_PAGE_NUMBER,
} from '../actions/lister-actions';
import { getCookieExperiments } from '../utilities/cookie-utilities';
import { parseFacetNameForAttribute } from '../utilities/facet-utilities';
import {
	formatSelectedFacetForTagging,
	formatSelectedFacetsForTagging,
	removeWhitespace,
} from '../utilities/lister-utilities';
import { getDefaultViewFormat } from '../utilities/storage-utilities';

const getInitialExperimentSwitches = () => {
	const initialExperimentSwitches = {
		...window?.AoListerPageExperiments,
		...getCookieExperiments(),
	};
	initialExperimentSwitches.CustomCategoriesEnabled = false;
	return initialExperimentSwitches;
};

const getInitialState = () => {
	const initialFeatureSwitches = window?.AO?.ListerFeatureSwitches;
	const initialExperimentSwitches = getInitialExperimentSwitches();

	return {
		adobeTagging: {},
		products: [],
		currentPageNumber: 1,
		pages: { firstPage: 1, pagesLoaded: 0, Pages: [] },
		pageTitle: '',
		pageDescription: '',
		sorting: {},
		seoHtml: {},
		banners: {},
		facets: [],
		breadcrumb: [],
		resetAllUrl: '',
		dismissFiltersUrl: window.location.pathname,
		filtersUrl: '',
		error: false,
		loading: true,
		richSnippetTotalProductCount: 0,
		richSnippetText: '',
		facetNameToScrollTo: '',
		categoryId: '',
		productsInBasket: '',
		productCardView: '',
		comparedProducts: [],
		selectedFacets: [],
		featureSwitches: initialFeatureSwitches,
		experimentSwitches:
			{
				...initialExperimentSwitches,
				CustomCategoriesEnabled:
					initialFeatureSwitches?.CustomCategoriesKillSwitch
						? false
						: initialExperimentSwitches.CustomCategoriesEnabled,
			} ?? {},
		highDemandProducts: window?.AO?.HighDemandProducts ?? [],
		isDealsPage: false,
		timeStamps: {},
		dealsTheme: window?.AO?.DealsTheme,
		resultsSummary: {},
		drawer: {
			drawerName: '',
			drawerDetail: '',
			drawerOpen: false,
			initialized: false,
		},
		financeSlideout: {
			productId: 1,
			productSku: 1,
			nowPrice: 1,
			memberPrice: 1,
			annualMembershipFee: 1,
			financePromotionCodes: [],
			memberUpfrontPaymentAmount: 0,
			upfrontPaymentAmount: 0,
			upfrontPaymentPercentage: 0,
			isInStock: false,
			switch24Pricing: {},
		},
	};
};

const initialState = getInitialState();
export { initialState };

function listerReducer(currentState, action = {}) {
	const state = currentState || getInitialState();

	switch (action.type) {
		case GOT_LISTER: {
			const {
				Pages,
				PageTitle,
				PageDescription,
				SeoHtml,
				CanonicalLink,
				Banners,
				BreadCrumb,
				Products,
				Sorting,
				Facets,
				ResetAllUrl,
				RichSnippetTotalProductCount,
				RichSnippetText,
				ApplianceTypeId,
				ShowAllProductsDetailLink,
				ShowAllProductsDetailText,
				AdobeTaggingViewModel,
				IsDeals,
				AddToBasketTimeStamp,
				AddToBasketTimeStampHash,
				ResultsSummary,
			} = action.payload;

			let pages = {
				...Pages,
				firstPage: Pages.CurrentPageIndex,
				pagesLoaded: Pages.CurrentPageIndex,
			};

			// Removes whitespace from facet and facetValue name
			if (Facets && Facets.length > 0) {
				Facets.forEach(function (facet) {
					facet.Name = removeWhitespace(facet.Name);

					facet.FacetValues.forEach(function (facetValue) {
						facetValue.Name = removeWhitespace(facetValue.Name);
					});
				});
			}

			return Object.assign({}, state, {
				adobeTagging: AdobeTaggingViewModel,
				products: Products.filter(
					(product) => product.State !== 'DiscontinuedNoAlt'
				),
				pages,
				pageTitle: PageTitle,
				pageDescription: PageDescription,
				sorting: Sorting,
				seoHtml: SeoHtml,
				canonical: CanonicalLink,
				banners: Banners,
				breadcrumb: BreadCrumb,
				facets: Facets,
				resetAllUrl: `${ResetAllUrl}${
					Sorting?.CurrentSortBy ? '?sort=' + Sorting?.CurrentSortBy : ''
				}`,
				loading: false,
				currentPageNumber: pages.CurrentPageIndex,
				richSnippetTotalProductCount: RichSnippetTotalProductCount,
				richSnippetText: RichSnippetText,
				categoryId: ApplianceTypeId,
				showAllProductsDetailLink: ShowAllProductsDetailLink,
				showAllProductsDetailText: ShowAllProductsDetailText,
				isDealsPage: IsDeals,
				productCardView: getDefaultViewFormat(IsDeals),
				timeStamps: {
					addToBasketTimeStamp: AddToBasketTimeStamp,
					addToBasketTimeStampHash: AddToBasketTimeStampHash,
				},
				resultsSummary: ResultsSummary,
			});
		}

		case GOT_NEXT_PAGE: {
			let { Pages, Products } = action.payload;

			let pages = {
				...Pages,
				firstPage: Math.min(state.pages.firstPage, Pages.CurrentPageIndex),
				pagesLoaded: Math.max(state.pages.pagesLoaded, Pages.CurrentPageIndex),
				Pages: [...state.pages.Pages, ...Pages.Pages].reduce((acc, v) => {
					if (acc.map(({ Index }) => Index).includes(v.Index)) {
						return acc;
					}
					return [...acc, v];
				}, []),
			};

			return Object.assign({}, state, {
				products: [
					...state.products,
					...Products.filter(
						(product) => product.State !== 'DiscontinuedNoAlt'
					),
				],
				pages,
				loading: false,
			});
		}

		case IS_LOADING: {
			const bool = action.payload;

			return Object.assign({}, state, {
				loading: bool,
			});
		}

		case NEW_DISMISS_FILTERS_URL: {
			const newUrl = action.payload;

			return Object.assign({}, state, {
				dismissFiltersUrl: newUrl,
			});
		}

		case NEW_FILTERS_URL: {
			const newUrl = action.payload;

			return Object.assign({}, state, {
				filtersUrl: newUrl,
			});
		}

		case UPDATE_CURRENT_PAGE_NUMBER: {
			return Object.assign({}, state, {
				currentPageNumber: action.payload,
				pages: { ...state.pages, CurrentPageIndex: action.payload },
			});
		}

		case FACET_NAME_TO_SCROLL_TO: {
			const facetNameToScrollTo = action.payload;

			return Object.assign({}, state, {
				facetNameToScrollTo: parseFacetNameForAttribute(facetNameToScrollTo),
			});
		}

		case GOT_PRODUCTS_IN_BASKET: {
			const productsInBasket = action.payload;

			return Object.assign({}, state, {
				productsInBasket: productsInBasket,
			});
		}

		case SET_PRODUCT_CARD_VIEW: {
			const viewType = action.payload;

			return Object.assign({}, state, {
				productCardView: viewType,
			});
		}

		case ADD_PRODUCT_TO_COMPARE: {
			const product = {
				sku: action.payload.sku,
				category: action.payload.category,
			};

			return Object.assign({}, state, {
				comparedProducts: [...state.comparedProducts, product],
			});
		}

		case REMOVE_PRODUCT_FROM_COMPARE: {
			const productToRemoveFromCompare = {
				sku: action.payload.sku,
				category: action.payload.category,
			};
			const newComparedProducts = [...state.comparedProducts].filter(function (
				item
			) {
				if (item.sku !== productToRemoveFromCompare.sku) {
					return item;
				}
				return false;
			});

			return Object.assign({}, state, {
				comparedProducts: newComparedProducts,
			});
		}

		case GOT_COMPARED_PRODUCTS: {
			return Object.assign({}, state, {
				comparedProducts: action.payload,
			});
		}

		case FACETS_SELECTED: {
			const { facetTypeHeader, facetValues } = action.payload;

			let selectedFacetsArray = [...state.selectedFacets];

			facetValues.forEach((facetValue) => {
				// check if the facet is within the selectedFacets array
				const facetToCheck = `${facetTypeHeader}|${facetValue.Name}`;
				const facetIsAlreadySelected = [...state.selectedFacets]
					.map(function (facet) {
						return facet.includes(facetToCheck);
					})
					.includes(true);

				// if it is in the selectedFacets array, remove it and assign to state
				if (facetIsAlreadySelected) {
					const selectedFacetsRemoved = [...state.selectedFacets].filter(
						function (facet) {
							const facetIsSelected = facet.includes(
								`${facetTypeHeader}|${facetValue.Name}`
							);
							return facetIsSelected === false;
						}
					);
					selectedFacetsArray = [...selectedFacetsRemoved];
				}

				// if it isn't in the selectedFacets array
				// format the facet for tagging purposes
				const formattedFacetString = formatSelectedFacetsForTagging(
					action.payload
				);

				// if formattedFacetString exists, add it to the selectedfacets array and assign to state
				if (formattedFacetString.length > 0) {
					selectedFacetsArray = [...selectedFacetsArray, formattedFacetString];
				}
			});

			return Object.assign({}, state, {
				selectedFacets: [...selectedFacetsArray],
			});
		}

		case FACET_SELECTED: {
			const { facetTypeHeader, facetValue } = action.payload;

			// TODO: Check if this block is doing anything
			// if the facet is already selected on page load, don't add the facet to the selectedFacets array
			if (state.selectedFacets.length === 0 && facetValue.Selected) {
				return Object.assign({}, state, {
					selectedFacets: [],
				});
			}

			// check if the facet is within the selectedFacets array
			const facetToCheck = `${facetTypeHeader}|${facetValue.Name}`;
			const facetIsAlreadySelected = [...state.selectedFacets]
				.map(function (facet) {
					return facet.includes(facetToCheck);
				})
				.includes(true);

			// if it is in the selectedFacets array, remove it and assign to state
			if (facetIsAlreadySelected) {
				const selectedFacetsRemoved = [...state.selectedFacets].filter(
					function (facet) {
						const facetIsSelected = facet.includes(
							`${facetTypeHeader}|${facetValue.Name}`
						);
						return facetIsSelected === false;
					}
				);

				return Object.assign({}, state, {
					selectedFacets: selectedFacetsRemoved,
				});
			}

			// if it isn't in the selectedFacets array
			// format the facet for tagging purposes
			const formattedFacetString = formatSelectedFacetForTagging(
				action.payload
			);

			// if formattedFacetString exists, add it to the selectedfacets array and assign to state
			if (formattedFacetString.length > 0) {
				return Object.assign({}, state, {
					selectedFacets: [...state.selectedFacets, formattedFacetString],
				});
			}

			// else if formattedFacetString doesn't exist, just give back the current selectedFacets state
			return Object.assign({}, state, {
				selectedFacets: [...state.selectedFacets],
			});
		}

		case CLEAR_ALL_SELECTED_FACETS: {
			return Object.assign({}, state, {
				selectedFacets: [],
			});
		}

		case SHOW_FILTERED_PRODUCTS: {
			return Object.assign({}, state, {
				selectedFacets: [],
			});
		}

		case SET_ACTIVE_DRAWER:
			return {
				...state,
				drawer: {
					drawerName: action.payload,
					drawerDetail: action.detail,
					drawerOpen: true,
					initialized: true,
				},
			};

		case OPEN_FINANCE_SLIDEOUT:
			return {
				...state,
				financeSlideout: action.payload,
			};

		case CLOSE_DRAWER:
			return {
				...state,
				drawer: {
					...state.drawer,
					drawerName: '',
					drawerDetail: '',
					drawerOpen: false,
				},
			};

		case SET_EXPERIMENT_SWITCHES:
			return {
				...state,
				experimentSwitches: { ...state.experimentSwitches, ...action.payload },
			};

		default:
			return state;
	}
}

export default listerReducer;
